import { Injectable, Type } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Params, Route, Router, Routes } from '@angular/router';
import { BehaviorSubject, filter, map, Observable } from 'rxjs';
import { urlJoin } from 'url-join-ts';
import { SelectRandomCrosswordsComponent } from '../../components/administrator/select-random-crosswords/select-random-crosswords.component';
import { BuildCrosswordComponent } from '../../components/build-crossword/build-crossword.component';
import { GenerateQuestionsComponent } from '../../components/build-crossword/create-edit-question/generate-questions.component';
import { CrosswordLandingComponent } from '../../components/crossword-landing/crossword-landing.component';
import { PlayCrosswordComponent } from '../../components/gameplay/play-crossword/play-crossword.component';
import { ListCrosswordBuildsComponent } from '../../components/list-crossword-builds/list-crossword-builds.component';
import { ListCrosswordGamesComponent } from '../../components/list-crossword-games/list-crossword-games.component';
import { ListCrosswordsComponent } from '../../components/list-crosswords/list-crosswords.component';
import { ShowEditQuestionComponent } from '../../components/show-edit-question/show-edit-question.component';
import { CreateUserComponent } from '../../components/user/create-user/create-user.component';
import { ForgotPasswordComponent } from '../../components/user/forgot-password/forgot-password.component';
import { LoginComponent } from '../../components/user/login/login.component';
import { PlayerSettingsComponent } from '../../components/user/player-settings/player-settings.component';
import { CanDeactivateComponent } from '../../components/util/can.deactivate.component';
import { StringUtil } from '../../components/util/string.util';
import { CheckLoggedIn } from '../../util/check.loggedin';
import { CheckLoggedInAdministrator } from '../../util/check.loggedin.administrator';

interface NavigationData {
  params: Params;
  queryParams: Params;
  url: string;
}

abstract class NavigateRouter<T> implements INavigateRouter<T> {
  private currentNavigation: BehaviorSubject<NavigationData>;
  private navigateByUrl: (url: string) => Promise<boolean>;
  constructor(protected readonly data: RouterData) {}

  initialize(navigateByUrl: (url: string) => Promise<boolean>, currentNavigation: BehaviorSubject<NavigationData>) {
    this.navigateByUrl = navigateByUrl;
    this.currentNavigation = currentNavigation;
  }

  protected getBaseUrl() {
    return new URL(this.data.path, window.location.origin);
  }

  protected abstract getValueFromParams(value: NavigationData): T;
  protected abstract getUrl(value: T): string;
  protected handlesCurrentNavigation(navigationData: NavigationData): boolean {
    const urlToCheck = StringUtil.getStringBeforeChar(navigationData.url, '/');
    return this.data.path === urlToCheck;
  }

  getParams(): T {
    const value = this.currentNavigation.getValue();
    if (!this.handlesCurrentNavigation(value)) {
      throw new Error(`Calling getValue on wrong route: ${this.data.path}`);
    }
    return this.getValueFromParams(value);
  }

  isCurrentRouter() {
    return this.handlesCurrentNavigation(this.currentNavigation.getValue());
  }

  navigatedTo() {
    return this.currentNavigation
      .pipe(filter((data) => this.handlesCurrentNavigation(data)))
      .pipe(map((data) => this.getValueFromParams(data)));
  }

  navigateTo(value: T): Promise<boolean> {
    return this.navigateByUrl(this.getUrl(value));
  }

  getRoute(): Route {
    const path = this.data.path + (this.data.param ? `/:${this.data.param}` : '');
    return {
      path: path,
      component: this.data.routeComponent,
      canDeactivate: this.data.checkDeactivate ? [CanDeactivateComponent] : null,
      canActivate: this.canActivateRouteCheck(),
      pathMatch: 'full',
    };
  }

  private canActivateRouteCheck(): any[] {
    if (this.data.checkActivateAdministrator) {
      return [CheckLoggedInAdministrator];
    }
    return this.data.checkActivate ? [CheckLoggedIn] : null;
  }
}

class EmptyRouter extends NavigateRouter<void> {
  constructor(readonly data: RouterData) {
    super(data);
  }

  protected getUrl() {
    const url = this.getBaseUrl();
    return url.pathname;
  }

  protected getValueFromParams() {
    return;
  }
}

class StringParamRouter extends NavigateRouter<string> {
  constructor(readonly data: RouterData) {
    super(data);
  }

  protected getUrl(value: string) {
    const url = this.getBaseUrl();
    return urlJoin(url.pathname, value);
  }

  protected getValueFromParams(value: NavigationData) {
    const valueAsString: string = value.params[this.data.param];
    return valueAsString ? valueAsString : '';
  }
}

class StringQueryParamRouter extends NavigateRouter<string> {
  constructor(readonly data: RouterData) {
    super(data);
  }

  protected getUrl(value: string) {
    const url = this.getBaseUrl();
    if (!value) {
      return url.pathname;
    }
    url.searchParams.append(this.data.queryParam, value);
    return url.pathname + url.search;
  }

  protected getValueFromParams(value: NavigationData) {
    const valueAsString: string = value.queryParams[this.data.queryParam];
    return valueAsString ? valueAsString : null;
  }
}

export interface ParamQueryData<T> {
  param: string;
  queryParam?: T;
}

export interface ConvertQueryParam<T> {
  convertToString(queryParam: T): string;
  convertFromString(queryParam: string): T;
}

class StringParamQueryRouter<T> extends NavigateRouter<ParamQueryData<T>> {
  constructor(
    readonly data: RouterData,
    private readonly convertQuery: ConvertQueryParam<T>,
  ) {
    super(data);
  }

  protected getUrl(value: ParamQueryData<T>) {
    const url = this.getBaseUrl();
    if (value.queryParam) {
      url.searchParams.append(this.data.queryParam, this.convertQuery.convertToString(value.queryParam));
    }
    return urlJoin(url.pathname, value.param + url.search);
  }

  protected getValueFromParams(value: NavigationData): ParamQueryData<T> {
    const paramAsString: string = value.params[this.data.param];
    const param = paramAsString ? paramAsString : '';
    const queryParamAsString: string = value.queryParams[this.data.queryParam];
    if (!queryParamAsString) {
      return { param };
    }
    return { param, queryParam: this.convertQuery.convertFromString(queryParamAsString) };
  }
}

export interface INavigateRouter<T> {
  navigatedTo(): Observable<T>;
  navigateTo(value: T): Promise<boolean>;
  getParams(): T;
  isCurrentRouter(): boolean;
  getRoute(): Route;
}

export interface RouterData {
  path: string;
  routeComponent?: Type<any>;
  checkActivate?: boolean;
  checkActivateAdministrator?: boolean;
  checkDeactivate?: boolean;
  param?: string;
  queryParam?: string;
}

@Injectable({ providedIn: 'root' })
export class RoutingService {
  private currentNavigation: BehaviorSubject<NavigationData>;
  public static login = new StringQueryParamRouter({ path: 'login', routeComponent: LoginComponent, queryParam: 'redirectUrl' });
  public static createUser = new StringQueryParamRouter({
    path: 'createUser',
    routeComponent: CreateUserComponent,
    queryParam: 'redirectUrl',
  });
  public static forgotPassword = new EmptyRouter({ path: 'forgotPassword', routeComponent: ForgotPasswordComponent });
  public static readonly listCrosswords = new EmptyRouter({
    path: 'listCrosswords',
    checkActivate: true,
    routeComponent: ListCrosswordsComponent,
  });
  public static readonly listCrosswordGames = new StringParamRouter({
    path: 'listCrosswordGames',
    checkActivate: true,
    routeComponent: ListCrosswordGamesComponent,
    param: 'type',
  });
  public static readonly listCrosswordBuilds = new EmptyRouter({
    path: 'listCrosswordBuilds',
    checkActivate: true,
    routeComponent: ListCrosswordBuildsComponent,
  });
  public static readonly playCrossword = new StringParamQueryRouter(
    {
      path: 'playCrossword',
      param: 'id',
      queryParam: 'join',
      checkActivate: true,
      checkDeactivate: true,
      routeComponent: PlayCrosswordComponent,
    },
    {
      convertToString: (queryParam: boolean) => String(queryParam),
      convertFromString: (queryParam: string) => queryParam === 'true',
    },
  );
  public static readonly crosswordLanding = new StringParamRouter({
    path: 'crossword',
    param: 'id',
    checkActivate: true,
    routeComponent: CrosswordLandingComponent,
  });
  public static readonly buildCrossword = new StringParamRouter({
    path: 'buildCrossword',
    param: 'id',
    checkActivate: true,
    checkDeactivate: true,
    routeComponent: BuildCrosswordComponent,
  });
  public static readonly playerSettings = new EmptyRouter({
    path: 'playersettings',
    checkActivate: true,
    routeComponent: PlayerSettingsComponent,
  });
  public static readonly generateQuestions = new EmptyRouter({
    path: 'generatequestions',
    checkActivate: true,
    routeComponent: GenerateQuestionsComponent,
  });
  public static readonly selectRandomCrosswords = new EmptyRouter({
    path: 'selectRandomCrosswords',
    checkActivateAdministrator: true,
    routeComponent: SelectRandomCrosswordsComponent,
  });
  public static readonly createEditQuestion = new StringParamQueryRouter(
    {
      path: 'createEditQuestion',
      param: 'id',
      queryParam: 'crosswordBuildId',
      checkActivate: true,
      checkDeactivate: true,
      routeComponent: ShowEditQuestionComponent,
    },
    {
      convertToString: (queryParam: string) => String(queryParam),
      convertFromString: (queryParam: string) => queryParam,
    },
  );

  constructor(
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  initialize() {
    this.currentNavigation = new BehaviorSubject<NavigationData>(this.getNavigationData());

    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => this.handleNavigation());

    for (const router of RoutingService.getRoutersToIterate()) {
      router.initialize((url) => {
        return this.router.navigateByUrl(url);
      }, this.currentNavigation);
    }
  }

  currentNavigation$(): Observable<NavigationData> {
    return this.currentNavigation.asObservable();
  }

  static getAppRoutes(): Routes {
    const defaultAppRoute = this.listCrosswords.getRoute();
    const routers = this.getRoutersToIterate();

    const routes = routers.map((r) => r.getRoute());
    const routesWithComponent = routes.filter((r) => r.component);
    return [...routesWithComponent, { path: '**', redirectTo: defaultAppRoute.path }];
  }

  private handleNavigation() {
    this.currentNavigation.next(this.getNavigationData());
  }

  private getNavigationData(): NavigationData {
    const params = RoutingService.collectRouteParams(this.router);
    const queryParams = this.route.snapshot.queryParams;
    return { params, queryParams, url: this.getUrlToCheck() };
  }

  private getUrlToCheck() {
    const url = new URL(this.router.url, window.location.origin);
    const path = url.pathname;
    return path.startsWith('/') ? path.substring(1) : path;
  }

  private static getRoutersToIterate(): NavigateRouter<unknown>[] {
    const routers: NavigateRouter<unknown>[] = [];
    for (const key of Object.keys(RoutingService)) {
      const router = (RoutingService as any)[key];
      if (router instanceof NavigateRouter) {
        routers.push(router);
      }
    }
    return routers;
  }

  private static collectRouteParams(router: Router) {
    let params = {};
    const stack: ActivatedRouteSnapshot[] = [router.routerState.snapshot.root];
    while (stack.length > 0) {
      const route = stack.pop()!;
      params = { ...params, ...route.params };
      stack.push(...route.children);
    }
    return params;
  }
}
